/**
 * @packageDocumentation
 * Bootstraps the Superhero Incognito experience by initialising PixiJS, wiring the preloader, loading assets, building UI layers, and orchestrating the main card screen.
 */
import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v8";
import { Assets } from "pixi.js";
import { StartAppResize } from "./resizer";
import { betArea } from "./Game/betArea";
// import * as PIXI3D from "pixi3d"
import { currencySymbol, ShowCent } from "./Game/server";
// import {instantWin} from "./Game/instantWin";
import { gsap } from "gsap/gsap-core";
import { BottomBar } from "./Game/bottomBar";
import { PixiPlugin } from "gsap/all";
import { UserOptions } from "./Game/settings";
gsap.registerPlugin(PixiPlugin);
PixiPlugin.registerPIXI(PIXI);
import { BMSwinProperties, GridProperties, HistoryProperties } from "./config";
import { canvasProperties } from "./config";
import { createStartPage } from "./Game/startPage";
import { createEndPage } from "./Game/endPage";
// import { initDevtools } from '@pixi/devtools';
import { initDevtools } from "@pixi/devtools";

import {
  applyCent,
  brightnessFilter,
  disabledfilter,
  font_main,
  shockwave,
} from "./Game/global";
import { Button } from "./lib/buttonCreator";
import { SoundManager } from "./Game/sounds";
import { backgroundAnimations } from "./Game/background";
import { Popup } from "./Game/popup";
import { electricShake } from "./lib/shakeEffect";
import { GameHistory } from "./Game/history";
import { Preloader } from "./preloader";
import { CardContainer, CardScreen, onSpinClicked } from "./Game/CardScreen";
import { specsFromRoundTuple } from "./Game/loadCards";
import { TotalWinButton } from "./Game/totalWinButton";
import { InfoButton } from "./Game/InfoButton";
import { PrizeChartButton, PrizeChartPopup } from "./Game/prizeChartButton";
//import { WinPopup } from "./Game/winpopup";
import { TestCasePanel } from "./Game/TestCasePanel";
import bottomWin from "./Game/bottomWin";
import { Bonus } from "./Game/Bonus"; 
import SettingsButton from "./Game/settingsButton";
import AutoPlayButton from "./Game/AutoPlayButton";
import { EnumEventBus, EventBus } from "./EventBus";
import { TransitionCanvas } from "./Game/Transition";

export let LogoAnim!: Spine & {
  //winPopup?: WinPopup;
  PopupStage?: Popup;
  // totalwinContainer?:TotalWinButton
  StartBut_enableFun?: () => void;
  StartBut_disableFun?: () => void;
  totalwinContainer?:TotalWinButton;
  bottomWin?: bottomWin;
  allsuper?:boolean;
  insideBonus?:boolean;
};
  export const overlay = document.createElement("div");

/**
 * Main application file for the game
 * @module app
 */

/**
 * Canvas width from properties
 * @constant {number}
 */
const app_width = canvasProperties.width;

/**
 * Canvas height from properties
 * @constant {number}
 */
const app_height = canvasProperties.height;

/**
 * Whether to resize canvas to device size
 * @constant {boolean}
 */
const resize_to_device = canvasProperties.resize;

/**
 * Color matrix filter for visual effects
 * @constant {PIXI.ColorMatrixFilter}
 */
const filter = new PIXI.ColorMatrixFilter();

/**
 * Secondary color matrix filter
 * @constant {PIXI.ColorMatrixFilter}
 */
const filter1 = new PIXI.ColorMatrixFilter();



/**
 * Total number of spins allowed
 * @type {number}
 */
let totalSpinCount = 6;

/**
 * Player's bet amount
 * @type {number}
 */
let playerBet = 2;

/**
 * Current round count
 * @type {number}
 */
let RoundCount = 0;

/**
 * Timeout for BMS win animation
 * @type {NodeJS.Timeout}
 */
let BME_TimeOut: NodeJS.Timeout;

/**
 * Function to adjust layout for portrait mode
 * @type {()=>void}
 */
let PortraitAdjustFun: () => void;

/**
 * Function to adjust layout for landscape mode
 * @type {()=>void}
 */
let landScapeAdjustFun: () => void;

/**
 * Function to adjust preloader for landscape mode
 * @type {()=>void}
 */
let landscapeAdjust_preloader: () => void;

/**
 * Function to adjust preloader for portrait mode
 * @type {()=>void}
 */
let PortraitAdjust_preloader: () => void;

/**
 * Whether BMS win is currently triggered
 * @type {boolean}
 */
let BMS_win_triggered = false;

/**
 * Main application initialization function
 * @async
 * @function app_main
 */

async function app_main() {
  /**
   * Creates the background stage for animations
   * @param app_width - Width of the application canvas
   * @param app_height - Height of the application canvas
   * @param resize_to_device - Whether to resize canvas to device size
   */
  const BgStage = new backgroundAnimations(
    app_width,
    app_height,
    resize_to_device
  );
  const PopupStage = new Popup(
    app_width,
    app_height,
    resize_to_device)
  /**
   * Main PIXI application instance
   * @type {PIXI.Application}
   */
  // <div id="overlay"></div>
  overlay.id = "overlay";
  overlay.style.zIndex = "1";
  overlay.style.display = "none";
  document.body.appendChild(overlay);

  const app: PIXI.Application = new PIXI.Application();

  /**
   * Initialize the PIXI application with configuration
   * @param width - Canvas width
   * @param height - Canvas height
   * @param backgroundAlpha - Background transparency (0 = transparent)
   * @param resolution - Canvas resolution multiplier
   */
  await app.init({
    width: app_width,
    height: app_height,
    backgroundAlpha: 0,
    resolution: 2,
  });
  document.body.appendChild(app.canvas); 
  app.renderer.canvas.style.zIndex = "2";
    (globalThis as any).__PIXI_APP__ = app;
    // (globalThis as any).__PIXI_STAGE__ = app.canvas;
  /**
   * Configure application resize handling
   * @param app - PIXI Application instance
   * @param app_width - Application width
   * @param app_height - Application height
   * @param resize_to_device - Whether to resize to device
   * @param transformOrigin_Y - Transform origin Y position
   * @param translate_Y - Translation Y amount
   * @param app_top - Top position of app
   * @param app_name - Name of application
   */
  StartAppResize({
    app: app,
    app_width: app_width,
    app_height: app_height,
    resize_to_device: resize_to_device,
    transformOrigin_Y: 50,
    translate_Y: 50,
    app_top: 50,
    app_name: "app",
  });

  /**
   * Initialize PIXI devtools for debugging
   * @param app - PIXI Application instance
   */
  initDevtools({
    app,
  });

  /**
   * Create and add preloader page
   * @param app_width - Application width
   * @param app_height - Application height
   * @param openGame - Callback function when loading completes
   */
  const preloaderPage = new Preloader(app_width, app_height, openGame);
  app.stage.addChild(preloaderPage);

  /**
   * Create bottom bar UI component
   * @type {BottomBar}
   */
  let BottomStage = await new BottomBar();
  const TransitionCnvs = await new TransitionCanvas()
  //....................................................................
  function openGame(this: any) {
    /**
     * Initializes the game by setting up background, sounds and UI elements
     */

    // Play background music and initialize background animations
    // if (preloaderPage.getSoundConsent()) {
    //   SoundManager.bgMusicSndPlay();
    // } else {
    //   SoundManager.MuteBgMusicFun();
    // }
    BgStage.formWideBg();
    BgStage.formCloudAnimations();
    BgStage.formWideBg_1();
    BgStage.formBgAnimations();
    BgStage.move_all_clouds();
    PopupStage.addWinPopup();
    PopupStage.addSettingsPopup();

    TransitionCnvs.createTransition();

    // Add bottom stage and remove preloader
    document.body.appendChild(BottomStage.app_bottom.canvas); 
    BottomStage.app_bottom.renderer.canvas.style.zIndex = "3";
    
    PopupStage.app_popup.renderer.canvas.style.zIndex = "10";
    
    app.stage.removeChild(preloaderPage);

    /**
     * Creates the outer frame sprite that contains the electric field animations
     * @type {PIXI.Sprite}
     */
  const total_container = new PIXI.Container();
  //  total_container.visible=false
  app.stage.addChild(total_container);

    let outerFrame = new PIXI.Sprite(Assets.get("reel_bg"));
    outerFrame.anchor.set(0.5);
    outerFrame.x = app_width / 2;
    outerFrame.y = app_height / 2;
    outerFrame.alpha = 1;
   app.stage.addChild(outerFrame);

    /**
     * Creates the outer frame sprite that contains the electric field animations
     * @type {PIXI.Sprite}
     */
    let innerFrame = new PIXI.Sprite(Assets.get("reelbg_1"));
    innerFrame.anchor.set(0.5);
    innerFrame.x = app_width / 2;
    innerFrame.y = app_height / 2 - 20;
    innerFrame.alpha = 1;
    app.stage.addChild(innerFrame);

    /**
     * Creates the start page instance and sets initial alpha
     */
    const startPage = new createStartPage();
    startPage.alpha = 0;

    /**
     * Creates and configures the start button
     * @param text - Button text "PLAY"
     * @param fontFamily - Font family to use
     * @param fontSize - Font size in pixels
     */
    const StartBut = new Button("Spin_but", {
      text: "",
      fontFamily: font_main,
      fontSize: 40,
    });
    StartBut.setTextPosition(0, -3);
    StartBut.x = 686;
    StartBut.y = 670;
    StartBut.scale.set(0.6);
    app.stage.addChild(StartBut);

    /**
     * Start button click handler function
     * Initializes game state and transitions to gameplay
     * @async
     */
    const startFun = async () => {
      console.log("testing ", this.cardscreen);
      if (this.cardscreen && this.cardscreen.screen) {
        LogoAnim.StartBut_disableFun!();
        onSpinClicked(this.cardscreen.screen);
      }

      // Play button click sound and enable gauge sounds
      SoundManager.Btnclick_1SndPlay();

      // Configure button and grid states
      // StartBut.btnGlowAnim.state.timeScale = 0;

      // StartBut.visible = false

      // Configure bet area
      BetAreaContainer.gameStarted = true;
      BetAreaContainer.visible = true;
      gsap.to(BetAreaContainer.plusContainer, { duration: 2, x: 0 });
      gsap.to(BetAreaContainer.minusContainer, { duration: 2, x: 0 });

      // Update bet display and balance
      BetAreaContainer.betval.text =
        "" + currencySymbol + BottomBar.TotalBetvalue.toFixed(2);
      applyCent(BetAreaContainer.betval, BottomBar.TotalBetvalue);
      BottomBar.TotalBalancevalue =
      BottomBar.TotalBalancevalue - BottomBar.TotalBetvalue;
      BottomBar.updateBalance(BottomBar.TotalBalancevalue);

      // Set initial spin count and show grid
      // SpinButton.spinCountNumber.text = 6;
      // startPage.visible = false;
      this.cardscreen.visible = true;
    };

    /**
     * Configure start button glow animation and interaction
     */
    StartBut.formBtnGlow({
      skeleton: "start_btn_anim_json",
      atlas: "start_btn_anim_atlas",
      scale: 1,
    });
    StartBut.addInteraction(startFun);
    StartBut.interactive = false;
    StartBut.btnGlowAnim.state.setAnimation(0, "animation", true);
    StartBut.btnGlowAnim.state.timeScale = 0;
    StartBut.btnGlowAnim.visible = false;

    gsap.to(StartBut, { duration: 0, pixi: { brightness: 0.5 } });

    // /**
    //  * Creates and configures the main game grid
    //  */
    // const grid = new Grid(GridProperties.TotalRows, GridProperties.TotalColumns);
    // grid.visible = true;

    // grid.pivot.set(startPage.width/2-GridProperties.boxWidth/2,startPage.height/2-GridProperties.boxHeight/2);
    // app.stage.addChild(grid);

  this.cardscreen = new CardContainer(app);

// this.cardscreen .visible=false
// total_container.visible=false
  // total_container.addChild(this.cardscreen);
      const screen = this.cardscreen.screen;

// this.cardscreen.screen.visible=false
    // screen?.on("roundstart", () => {
    //   StartBut.interactive = false;


    //   // StartBut.alpha = 0.6;
    // });

    // screen?.on("roundend", () => {
    //   StartBut.interactive = true;
    //   // StartBut.alpha = 1;
    // });
    // LogoAnim.totalwinContainer = totalwinContainer;
    // (optional) read the win and update your UI
    // screen?.on("win", ({ amount }) => {
    //   // example: update Total Win UI if you expose a setter (see below)
    //   totalwinContainer?.setValue?.(amount);
    // });

    // empty popup hook you can fill later anywhere:
    // (screen as any).showWinPopup = () => { /* TODO: open your popup */ };
    // cardscreen.position.set(canvasProperties.width/2, canvasProperties.height/2);

    let pageAssumed_x = 536.28;
    let pageAssumed_y = 426;
    let pageAssumed_px = startPage.width / 2 - GridProperties.boxWidth / 2;
    let pageAssumed_py = startPage.height / 2 - GridProperties.boxHeight / 2;

    /**
     * Configure start page position relative to grid
     */
    startPage.x = pageAssumed_x;
    startPage.y = pageAssumed_y;
    startPage.pivot.set(pageAssumed_px, pageAssumed_py);
    this.cardscreen.app = app;

    
    setTimeout(() => {
      gsap.to(BetAreaContainer.plusContainer, { duration: 0.5, x: 100 });
      gsap.to(BetAreaContainer.minusContainer, { duration: 0.5, x: -100 });
      StartBut.filters = [filter1];
      gsap.to(StartBut, {
        duration: 0.5,
        pixi: { brightness: 1 },
        onComplete: () => {
          StartBut.filters = [];
          StartBut.btnGlowAnim.state.timeScale = 1;
          StartBut.btnGlowAnim.visible = true;
        },
      });
      console.log("testing StartBut.interactive")
      StartBut.interactive = true;
      gsap.to(startPage, { duration: 0, alpha: 1 });
    }, 10);
    app.stage.addChild(startPage);
   
    PopupStage.addAutoplayPopup(() => this.cardscreen.screen); 
    // this.cardscreen.screen, () => onSpinClicked(this.cardscreen.screen

    // position it next to your Spin or Bet area (tweak for your layout)

    const autoPlayButton = new AutoPlayButton();

    autoPlayButton.scale.set(.7);
    app.stage.addChild(autoPlayButton);

// If you have a bottomBar container, add it there; else add to stage/UI layer

// (Optional) stop autoplay when user clicks Spin manually
StartBut?.on?.("pointertap", () => {
  EventBus.emit(EnumEventBus.setAutoplayGame, false);
});

// (Optional) expose for debug
    const testCasePanel = new TestCasePanel();
    (window as any).testCases = testCasePanel;


    /**
     * Creates and configures the infoButton UI component
     */
    const infoButton = new InfoButton();
    infoButton.pivot.set(infoButton.width / 2, infoButton.height / 2);
    infoButton.x = 493;
    infoButton.y = 102;
    infoButton.visible = true;
    app.stage.addChild(infoButton);

    /**
 
/**
 * Creates and configures the infoButton UI component
 */
    const prizeChartButton = new PrizeChartButton(app);
    prizeChartButton.pivot.set(
      prizeChartButton.width / 2,
      prizeChartButton.height / 2
    );
    prizeChartButton.x = 1127;
    prizeChartButton.y = 102;
    prizeChartButton.visible = true;
    app.stage.addChild(prizeChartButton);

    /**





/**
 * Creates and configures the Total bet UI component
 */
    const totalwinContainer = new TotalWinButton();
    totalwinContainer.pivot.set(
      totalwinContainer.width / 2,
      totalwinContainer.height / 2
    );
    totalwinContainer.x = 400;
    totalwinContainer.y = 738;
    totalwinContainer.visible = true;
    app.stage.addChild(totalwinContainer);

    /**
     * Creates and configures the bet area UI component
     */
    const BetAreaContainer = new betArea();
    BetAreaContainer.pivot.set(
      BetAreaContainer.width / 2,
      BetAreaContainer.height / 2
    );
    BetAreaContainer.x = 1086;
    BetAreaContainer.y = 738;
    BetAreaContainer.visible = true;
    app.stage.addChild(BetAreaContainer);
    (window as any).betArea = BetAreaContainer;
    BetAreaContainer.plusContainer.x = 0;
    BetAreaContainer.minusContainer.x = 0;
    app.stage.addChild(this.cardscreen); 

    let wasSpinning = false;

    /**
     * Update loop for spin button state
     */
    app.ticker.add(() => {
      if (!this.cardscreen.spinStarted) {
        if (wasSpinning) {
          wasSpinning = false;
        }
      } else {
        wasSpinning = true;
      }
    });

    /**
     * Handles keyboard space bar press
     * Triggers spin or stop based on visible button
     */
    document.addEventListener("keydown", (keycode) => {
      if (keycode.code == "Space") {
        if (StartBut.visible) {
        }
      }
    });

    /**
     * Creates BMS win animation using Spine
     * @type {Spine}
     */
    const BMS_animation = Spine.from({
      skeleton: "bms_win_anim_json",
      atlas: "bms_win_anim_atlas",
      scale: 0.5,
    });
    BMS_animation.x = app_width / 2 - 40;
    BMS_animation.y = app_height / 2 - 38;
    BMS_animation.state.setAnimation(0, "big_win_anim", true);
    BMS_animation.state.timeScale = 0;
    BMS_animation.visible = false;
    this.cardscreen.addChild(BMS_animation);
    this.cardscreen.BMS_animation = BMS_animation;

    /**
     * Configures BMS animation interaction properties
     */
    BMS_animation.interactive = false;
    BMS_animation.cursor = "pointer";

    /**
     * Handles BMS animation click event
     * Stops sounds and transitions to next state
     */
    BMS_animation.on("pointertap", () => {
      gsap.to(
        [
          SoundManager.big_win_snd,
          SoundManager.mega_win_snd,
          SoundManager.super_win_snd,
        ],
        {
          duration: 1,
          volume: 0,
          onComplete: () => {
            SoundManager.big_win_snd.stop();
            SoundManager.big_win_snd.volume(1);
            SoundManager.mega_win_snd.stop();
            SoundManager.mega_win_snd.volume(1);
            SoundManager.super_win_snd.stop();
            SoundManager.super_win_snd.volume(1);
          },
        }
      );
      clearTimeout(BME_TimeOut);
      BMS_animation.state.timeScale = 0;
      BMS_animation.visible = false;
      BMS_win_triggered = false;
      BMS_animation.interactive = false;
      this.cardscreen.BMS_win_triggered = BMS_win_triggered;
      if (!this.cardscreen.isThereMingame) {
        this.cardscreen.spinStarted = false;
      }
    });

    /**
     * Shows BMS animation with specified animation type
     * @param anim - Animation name to play
     * @param win - Win amount
     */
    BottomBar.showBmsAnim = (anim: string, win: number) => {
      console.log("special win opened");
      BMS_win_triggered = true;
      this.cardscreen.BMS_win_triggered = BMS_win_triggered;
      BMS_animation.visible = true;
      BMS_animation.state.setAnimation(0, anim, true);
      BMS_animation.state.timeScale = 1;
      BME_TimeOut = setTimeout(() => {
        gsap.to(
          [
            SoundManager.big_win_snd,
            SoundManager.mega_win_snd,
            SoundManager.super_win_snd,
          ],
          {
            duration: 1,
            volume: 0,
            onComplete: () => {
              SoundManager.big_win_snd.stop();
              SoundManager.big_win_snd.volume(1);
              SoundManager.mega_win_snd.stop();
              SoundManager.mega_win_snd.volume(1);
              SoundManager.super_win_snd.stop();
              SoundManager.super_win_snd.volume(1);
            },
          }
        );

        BMS_animation.interactive = false;
        BMS_animation.state.timeScale = 0;
        const endPage = new createEndPage();
        BMS_animation.visible = false;
        BMS_win_triggered = false;
        if (!this.cardscreen.isThereMingame) {
          this.cardscreen.spinStarted = false;
        }
        this.cardscreen.BMS_win_triggered = BMS_win_triggered;
      }, BMSwinProperties.stayTime);
    }; //.............................................................................
    /**
     * Creates and configures the end page for game completion
     * @type {createEndPage}
     */
    const endPage = new createEndPage();
    endPage.x = pageAssumed_x;
    endPage.y = pageAssumed_y;
    endPage.pivot.set(pageAssumed_px, pageAssumed_py);
    endPage.visible = false;

    /**
     * Handles hiding the end page and resetting game state
     * Resets counters, gauges, animations and UI elements
     */
    endPage.hideEndpage = () => {
      return
      gsap.killTweensOf(endPage.ContgratulationsTxt);
      gsap.killTweensOf(endPage.YouWonTxt);
      BottomBar.TotalBalancevalue =
        BottomBar.TotalBalancevalue + BottomBar.TotalWinvalue;
      BottomBar.updateBalance(BottomBar.TotalBalancevalue);
      BottomBar.updateTotalWin(0);
      endPage.collectButContainer.visible = false;
      RoundCount = 0;
      BetAreaContainer.gameStarted = false;
      endPage.visible = false;

      // Reset UI states and animations
      this.cardscreen.filters = [filter];
      StartBut.filters = [filter1];
      gsap.to(StartBut, { duration: 0, pixi: { brightness: 0.5 } });
      StartBut.visible = true;
      this.cardscreen.spinStarted = true;
      this.cardscreen.totalWin = 0;

      this.cardscreen.visible = true;
      startPage.visible = true;
      startPage.alpha = 0;

      // Delayed UI updates
      setTimeout(() => {
        gsap.to(BetAreaContainer.plusContainer, { duration: 0.5, x: 95 });
        gsap.to(BetAreaContainer.minusContainer, { duration: 0.5, x: -95 });
        StartBut.filters = [filter1];
        gsap.to(StartBut, {
          duration: 0.5,
          pixi: { brightness: 1 },
          onComplete: () => {
            StartBut.filters = [];
            StartBut.btnGlowAnim.state.timeScale = 1;
            StartBut.btnGlowAnim.visible = true;
          },
        });
        StartBut.interactive = true;
        gsap.to(startPage, { duration: 0.5, alpha: 1 });
      }, 1000);
    };

    /**
     * Creates and configures the end page transition animation
     * @type {Spine}
     */
    const endPageTransitionAnim = endPage.formEndPagePreAnim(endPage);
    endPageTransitionAnim.x = pageAssumed_x;
    endPageTransitionAnim.y = pageAssumed_y;
    app.stage.addChild(endPageTransitionAnim);
    app.stage.addChild(endPage);
    this.cardscreen.EndPage = endPage;

    /**
     * Shows the end page with transition animations
     */
    this.cardscreen.showEndpage = () => {
      this.cardscreen.isThereMingame = false;
      SoundManager.Final_transitionSndPlay();
      endPageTransitionAnim.state.setAnimation(0, "animation", false);
      endPageTransitionAnim.state.timeScale = 1;
      endPageTransitionAnim.visible = true;
      setTimeout(() => {
        this.cardscreen.visible = false;
      }, 500);
      setTimeout(() => {
        electricShake(outerFrame, 10, 0.05, 10, outerFrame.x, outerFrame.y);
        console.log(this.cardscreen.totalWin, "this.cardscreen.totalWin");
        endPage.UpdateEndWinText(this.cardscreen.totalWin);
      }, 1500);
    };

    /**
     * Creates options/settings button
     * @type {UserOptions}
     */
    // let OptionsIcon = new UserOptions();
    //OptionsIcon.x = app_width - 89;
    //OptionsIcon.y = 50;
    //app.stage.addChild(OptionsIcon);


    const settingsButton = new SettingsButton(app_width, app_height);
    app.stage.addChild(settingsButton);

    /**
     * Creates animated game logo using Spine
     * @type {Spine}
     */
    LogoAnim = Spine.from({
      skeleton: "logo_anim_json",
      atlas: "logo_anim_atlas",
      scale: 2,
    });
    LogoAnim.name = "Logo_Anim1"
    LogoAnim.x = app.screen.width / 2;
    LogoAnim.y = 70;
    LogoAnim.state.setAnimation(0, "idle_anim", true);
    LogoAnim.state.timeScale = 0.8;
    LogoAnim.scale.set(0.15);
    app.stage.addChild(LogoAnim);
    LogoAnim.insideBonus = false;


    LogoAnim.StartBut_enableFun = () => {
      gsap.to(BetAreaContainer.plusContainer, { duration: 0.5, x: 100 });
      gsap.to(BetAreaContainer.minusContainer, { duration: 0.5, x: -100 });
      StartBut.interactive = true;
      StartBut.filters = []
      autoPlayButton.interactive = true;
      autoPlayButton.setAutoBtnFilter([]);
    };
    LogoAnim.StartBut_disableFun = () => {
      gsap.to(BetAreaContainer.plusContainer, { duration: 0.5, x: 0 });
      gsap.to(BetAreaContainer.minusContainer, { duration: 0.5, x: 0 });
      StartBut.interactive = false;
      StartBut.filters = [disabledfilter]
      autoPlayButton.interactive = false;
      autoPlayButton.setAutoBtnFilter([disabledfilter]);
    };

    // screen!.LogoAnim = LogoAnim;
    /**
     * Plays win animation for logo
     */
    function playLogoWinAnim() {
      LogoAnim.state.setAnimation(0, "win_anim", false);
      LogoAnim.state.addAnimation(0, "idle_anim", true);
    }
    this.cardscreen.playLogoAnim = playLogoWinAnim;

    /**
     * Orientation state flags
     */
    let portrait = false;
    let Landscape = true;

    const BottomWin = new bottomWin()
    app.stage.addChild(BottomWin);
    BottomWin.x = app_width/2;
    BottomWin.y = app_height/2+app_height/3+30;
    BottomWin.visible = false;
    LogoAnim.bottomWin = BottomWin;
   // const Win_Popup = new WinPopup();
    //app.stage.addChild(Win_Popup);
    //LogoAnim.winPopup = Win_Popup;
    LogoAnim.PopupStage = PopupStage;
    PopupStage.winPopup!.LogoAnim = LogoAnim;
    //Win_Popup.LogoAnim = LogoAnim;
    LogoAnim.totalwinContainer = totalwinContainer;
    const prizePopup = new PrizeChartPopup(app);
    app.stage.addChild(prizePopup); 
    prizeChartButton.prizePopup=prizePopup

    const BonusPage = new Bonus();
    app.stage.addChild(BonusPage)
    BonusPage.app = app;
    BonusPage.bonusBg = BgStage.bgSprite;
    PopupStage.winPopup.BonusPage = BonusPage;
    //Win_Popup.BonusPage = BonusPage;
    setTimeout(() => {
      // const bonusData = [["power-up",1,4,3,5],[30,"power-up",50],["power-up",100],[1000]] 
      // BonusPage.startBonus(bonusData)

    }, 1000);
    // }, 1000);
    // Win_Popup.showWinpopup(0.3)
    /**
     * Adjusts layout for landscape orientation
     * Sets positions and scales for all game elements in landscape mode
     */  
    landScapeAdjustFun = () => {
      // Handle orientation change from portrait
      if (portrait == true) {
        Landscape = true;
        portrait = false;
      }
    BottomWin.x = app_width/2;
    BottomWin.y = app_height/2+app_height/3+30;
    BottomWin.scale.set(1);
    
    autoPlayButton.position.set(786, 680);
    autoPlayButton.scale.set(.7);

    infoButton.x = 493;
    infoButton.y = 102;
    infoButton.scale.set(1);

    prizeChartButton.x = 1127;
    prizeChartButton.y = 102;
    prizeChartButton.scale.set(1);

    totalwinContainer.x = 390;
    totalwinContainer.y = 738;
    totalwinContainer.scale.set(1);





    outerFrame.x = app_width / 2;
    outerFrame.y = app_height / 2;
    outerFrame.scale.set(1)
    
    innerFrame.x = app_width / 2;
    innerFrame.y = app_height / 2 - 20;
    innerFrame.scale.set(1)
    
      // Reset scales to 1 for landscape
      this.cardscreen.cardContainer.scale.set(1);
      this.cardscreen.cardContainer.x = 0;
      this.cardscreen.cardContainer.y = 0;
      startPage.scale.set(1);
      endPage.scale.set(1); 
      StartBut.scale.set(0.6);
      endPageTransitionAnim.scale.set(1);

      // Position main game elements
      pageAssumed_x = app_width / 2;
      pageAssumed_y = app_height / 2;
      startPage.x = pageAssumed_x;
      startPage.y = pageAssumed_y;
      endPage.x = pageAssumed_x;
      endPage.y = pageAssumed_y;
      outerFrame.x = pageAssumed_x;
      outerFrame.y = pageAssumed_y;
      endPageTransitionAnim.x = pageAssumed_x;
      endPageTransitionAnim.y = pageAssumed_y;
      StartBut.x = 686;
      StartBut.y = 670;

      // Configure bet area container
      BetAreaContainer.scale.set(1);
      // BetAreaContainer.pivot.set(
      //   BetAreaContainer.width / 2,
      //   BetAreaContainer.height / 2
      // );
      BetAreaContainer.x = 1138;
      BetAreaContainer.y = 738;

      settingsButton.landScapeAdjust();
      // Position options icon
      //OptionsIcon.scale.set(1);
      //OptionsIcon.x = app_width - 89;
      //OptionsIcon.y = 50;
      PopupStage.settingsPopup.landscapeAdjust();


      // Position logo animation
      LogoAnim.x = app.screen.width / 2 + 10;
      LogoAnim.y = 70;
      LogoAnim.state.timeScale = 0.8;
      LogoAnim.scale.set(0.15);
    };

    /**
     * Adjusts layout for portrait orientation
     * Sets positions and scales for all game elements in portrait mode
     */
    PortraitAdjustFun = () => {
      // Handle orientation change from landscape
      if (Landscape) {
        Landscape = false;
        portrait = true;
      }

    autoPlayButton.position.set(768, 684);
    autoPlayButton.scale.set(.5);

    infoButton.x = 580;
    infoButton.y = 250;
    infoButton.scale.set(0.55);

    prizeChartButton.x = 916;
    prizeChartButton.y = 250;
    prizeChartButton.scale.set(0.55);

    totalwinContainer.x = 525;
    totalwinContainer.y = 580;
    totalwinContainer.scale.set(0.55);

    BottomWin.x = 687.5// app_width/2;
    BottomWin.y = 544.5//app_height/2+app_height/3+30;
    BottomWin.scale.set(0.675);
    

    outerFrame.x = 674// app_width / 2;
    outerFrame.y = 348.5//app_height / 2;
    outerFrame.scale.set(0.5)
    
    innerFrame.x = 674//app_width / 2;
    innerFrame.y = 381.5//app_height / 2 - 20;
    innerFrame.scale.set(0.525)


      // Set scales for portrait mode
      this.cardscreen.cardContainer.x = 321;
      this.cardscreen.cardContainer.y = 190;
      this.cardscreen.cardContainer.scale.set(0.525);
      startPage.scale.set(0.6);
      endPage.scale.set(0.6);
      // outerFrame.scale.set(0.6);
      endPageTransitionAnim.scale.set(0.6);

      // Position main game elements
      pageAssumed_x = app_width / 2;
      pageAssumed_y = app_height / 2;
      startPage.x = pageAssumed_x;
      startPage.y = pageAssumed_y;
      endPage.x = pageAssumed_x;
      endPage.y = pageAssumed_y;
      outerFrame.x = pageAssumed_x;
      outerFrame.y = pageAssumed_y;
      endPageTransitionAnim.x = pageAssumed_x;
      endPageTransitionAnim.y = pageAssumed_y;

      // Configure start button
      StartBut.scale.set(0.6);
      StartBut.x = 1366 / 2;
      StartBut.y = 768 - 102;

      // Configure bet area container
      BetAreaContainer.scale.set(0.55);
      // BetAreaContainer.pivot.set(
      //   BetAreaContainer.width / 2,
      //   BetAreaContainer.height / 2
      // );
      BetAreaContainer.x = 925.152//1366 / 2 + BetAreaContainer.width / 2 + 132;
      BetAreaContainer.y = 580//768 - 150 + BetAreaContainer.height / 2 + 30;
      
      settingsButton.portraitAdjust();
      // Position options icon
      //OptionsIcon.scale.set(0.66);
      //OptionsIcon.x = 663 //app_width / 2 + 195;
      //OptionsIcon.y = 675 //20;


      PopupStage.settingsPopup.portraitAdjust();

    //  PopupStage.settingsPopup.settingsScaleControler.x = 459//pageAssumed_x - OptionsIcon.x;
    //  PopupStage.settingsPopup.settingsScaleControler.y = 473//pageAssumed_y - OptionsIcon.y;
      //OptionsIcon.settingsScaleControler.scale.set(1);
      //OptionsIcon.settingsScaleControler.x = -459//pageAssumed_x - OptionsIcon.x;
      //OptionsIcon.settingsScaleControler.y = 473.5//pageAssumed_y - OptionsIcon.y;

      // Position logo animation
      LogoAnim.scale.set(0.145);
      LogoAnim.x = app_width / 2 + 10 // app_width / 2 - 145;
      LogoAnim.y = 92.5//768 - 100;
    };

    // Initial orientation check and setup
    if (window.matchMedia("(orientation:portrait)").matches) {
      portrait = true;
      Landscape = false;
      PortraitAdjustFun();
    } else {
      portrait = false;
      Landscape = true;
      landScapeAdjustFun();
    }
  }

}

/**
 * Exports for layout adjustment functions
 */
export {
  PortraitAdjustFun,
  landScapeAdjustFun,
  PortraitAdjust_preloader,
  landscapeAdjust_preloader,
};

app_main();
